import React, { useMemo } from 'react';
import ServiceCatalog from '../../catalog/ServiceCatalog';
import CustomerCard from '../CustomerCard';
import { Notice } from '../../common/Feedback';
import { BranchIcon } from '../../common/icons';
import { customerView } from '../../../utils/customer';
import { serviceKey } from '../../../utils/services';

/** Step 2 — pick services from the bill's branch price list. */
export default function ServicesStep({ bill, catalog, lines, onToggle, onChangeBranch, branchName }) {
  const selectedKeys = useMemo(() => new Set(lines.map((l) => l.key)), [lines]);

  // Past services that this branch still sells — one tap to add again.
  const again = useMemo(() => {
    const c = customerView(bill);
    if (!c) return [];
    const byKey = new Map(catalog.services.map((s) => [serviceKey(s.name), s]));
    const seen = new Set();
    return c.previousServices
      .map((p) => byKey.get(serviceKey(p.service_name)))
      .filter((s) => s && !seen.has(serviceKey(s.name)) && seen.add(serviceKey(s.name)))
      .slice(0, 8);
  }, [bill, catalog.services]);

  const missing = lines.filter((l) => l.price_missing && !(Number(l.unit_price) > 0));

  return (
    <div className="dp-step-body">
      <div className="dp-step-head dp-step-head--row">
        <div>
          <h2>Services</h2>
          <p className="dp-help">Tap services to add or remove them. Set stylist, quantity and offers in the order panel.</p>
        </div>
        <button type="button" className="dp-branch-chip" onClick={onChangeBranch} title="Change the branch for this bill">
          <BranchIcon size={16} />
          <span>
            <small>Branch</small>
            <strong>{branchName || 'Pick a branch'}</strong>
          </span>
          <em>Change</em>
        </button>
      </div>

      <CustomerCard bill={bill} compact />

      {missing.length ? (
        <Notice tone="warn">
          No price for {missing.map((l) => `“${l.name}”`).join(', ')} at this branch. Type the price in the order panel, or remove it and
          pick the matching service below.
        </Notice>
      ) : null}

      {again.length ? (
        <section className="dp-again">
          <p className="dp-label">Taken before</p>
          <div className="dp-chips">
            {again.map((s) => (
              <button
                key={s.id || s.name}
                type="button"
                className={`dp-chip-btn${selectedKeys.has(serviceKey(s.name)) ? ' is-on' : ''}`}
                onClick={() => onToggle(s)}
              >
                {s.name}
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {bill.franchise_id ? (
        <ServiceCatalog
          services={catalog.services}
          loading={catalog.loading}
          error={catalog.error}
          selectedKeys={selectedKeys}
          onToggle={onToggle}
        />
      ) : (
        <Notice tone="warn" action={<button type="button" className="dp-btn" onClick={onChangeBranch}>Pick branch</button>}>
          Pick the branch for this bill to see its services.
        </Notice>
      )}
    </div>
  );
}
